<template>
    <div>
        <div v-if="type.global">
            <div class="is-align-center auto-save">
                <div v-if="hasErrors && !saving" class="auto-save__global__error" role="status">
                    <span class="has-text-danger">Save failed</span>

                    <e-icon icon="exclamation-triangle" class="has-text-danger" />
                </div>

                <div v-else class="auto-save__global__status" role="status">
                    <span v-if="!isEmpty" class="auto-save__global__content">
                        <span class="auto-save__global__label">{{ message }}</span>
                        <span class="auto-save__icon-slot" aria-hidden="true">
                            <ProgressSpinner
                                v-if="saving || courseKeyIntegrationInProgress"
                                stroke-width="3"
                                animation-duration=".8s"
                                class="auto-save__spinner"
                            />
                            <e-icon
                                v-else-if="saved && !hasErrors"
                                icon="check"
                                class="has-text-success auto-save__saved-icon"
                            />
                        </span>
                    </span>
                </div>
            </div>
        </div>

        <div v-else>
            <div v-if="type.header">
                <div v-show="status" class="has-text-weight-normal has-text-danger tw:whitespace-nowrap">
                    <small>*Missing Required Information</small>
                </div>
            </div>

            <div v-if="!type.header" class="is-flex" :class="{ 'is-block': type.list }">
                <div
                    v-for="item in status"
                    :key="item"
                    class="has-text-weight-normal has-text-danger"
                    :class="{ 'm-none': type.list }"
                >
                    <small>{{ item }}</small>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import filter from 'lodash/filter.js';
import get from 'lodash/get.js';
import isEmpty from 'lodash/isEmpty.js';
import values from 'lodash/values.js';
import ProgressSpinner from 'primevue/progressspinner';
import { createNamespacedHelpers } from 'vuex';

const { mapState, mapGetters } = createNamespacedHelpers('AutoSave');

export default {
    name: 'AutoSave',

    components: { ProgressSpinner },

    props: {
        type: {
            default: () => {
                return {
                    global: true,
                };
            },
            type: Object,
            required: false,
        },

        indicator: {
            default: null,
            required: false,
            type: String,
        },

        status: {
            default: null,
            required: false,
            type: Object,
        },

        id: {
            type: [String, Number],
            required: false,
            default: null,
        },
    },

    computed: {
        ...mapState({
            indicators: state => state.indicators,
            autosaveErrors: 'errors',
        }),

        ...mapGetters(['isEmpty', 'validationErrors']),

        saving() {
            if (this.indicator) {
                const saver = this.indicators[this.indicator];

                return get(saver, 'message') === 'Saving...';
            }
            return filter(values(this.indicators), item => item.saved === false).length > 0;
        },

        message() {
            if (this.indicator) {
                const saver = this.indicators[this.indicator];

                return get(saver, 'message');
            }

            return get(values(this.indicators), '0.message');
        },

        saved() {
            if (this.indicator) {
                return get(this.indicators[this.indicator], 'saved');
            }

            return this.$store.getters['AutoSave/allSaved'];
        },

        /**
         * Check if there are any autosave errors.
         *
         * For the global indicator (type.global), this intentionally shows "Save failed"
         * for ANY autosave error without filtering by indicator or activeId. This ensures
         * validation errors are always visible to the user regardless of which field
         * triggered the error.
         */
        hasErrors() {
            return !isEmpty(this.autosaveErrors);
        },

        active() {
            const activeId = this.$store.getters['AutoSave/getActiveId'];

            return this.id === activeId;
        },

        courseKeyIntegrationInProgress() {
            return this.$store.state.CourseTemplateForm?.courseKeyIntegrationInProgress || false;
        },
    },

    watch: {
        validationErrors(list) {
            if (list.length > 0) {
                // Show validation errors with field context using snackbar for better visibility
                list.forEach(error => {
                    const message = typeof error === 'string' ? error : error.formatted || error.message;

                    // Only show snackbar if message is a non-empty string
                    if (message && typeof message === 'string' && message.trim()) {
                        this.$snackbar.open({
                            duration: 15000,
                            message: `Validation Error: ${message}`,
                            position: 'is-bottom-left',
                            type: 'is-danger',
                            actionText: 'Dismiss',
                        });
                    }
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

.auto-save {
    min-width: 170px;
    min-height: 0;
    display: inline-flex;
    align-items: center;
}

.auto-save__global__error {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    white-space: nowrap;
    border: 0 !important;
    cursor: default;

    &:hover {
        color: $text !important;
    }

    .icon i::before {
        position: relative;
        top: -2px;
    }
}

.auto-save__global__status {
    // Reserve the same status box for idle, saving and saved without button styling.
    width: 7.25rem;
    height: 2.5em;
    min-height: 2.5em;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25;
    box-shadow: none;
    background: transparent;
    border: 0;
    cursor: default;

    .icon i::before {
        position: relative;
        top: -2px;
    }
}

.auto-save__global__content {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 0.375rem;
}

.auto-save__global__label {
    flex: 0 0 auto;
    white-space: nowrap;
}

.auto-save__icon-slot {
    display: flex;
    width: 1.25rem;
    height: 1.25rem;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.auto-save__spinner {
    width: 1.25rem !important;
    height: 1.25rem !important;
}

.auto-save__icon-slot :deep(.p-progressspinner-circle) {
    stroke: $primary !important;
    animation: p-progressspinner-dash 1.5s ease-in-out infinite;
}

.auto-save__saved-icon {
    font-size: 1rem;
    line-height: 1;
}
</style>
